import {
  ADMISSION_STATUSES,
  BED_BOARD_STATES,
  BED_STATUSES,
  BOARD_SETTABLE_STATUSES,
  DISCHARGE_DISPOSITIONS,
  HOUSEKEEPING_TASK_STATUSES,
} from '@wodi/domain';
import { z } from 'zod';

import { ageSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { amountSchema } from './money.ts';

// Inpatient ADT and the bed board (docs/plans/phase2-inpatient-adt.md §3). Wards, rooms and
// beds are facility master data; admissions, the location chain and bed days hang from the
// inpatient visit. Only internal IDs travel; names and clinical text never reach a URL.

export {
  ADMISSION_STATUSES,
  BED_BOARD_STATES,
  BED_STATUSES,
  BOARD_SETTABLE_STATUSES,
  DISCHARGE_DISPOSITIONS,
};

const name = z.string().trim().min(1).max(80);
const bedLabel = z.string().trim().min(1).max(20);
/** Clinical free text: stored, shown to clinicians, never logged. */
const clinicalText = z.string().trim().min(1).max(4000);

// --- Settings: wards, rooms and beds ---------------------------------------------------------

export const adminBedSchema = z.object({
  id: z.uuid(),
  wardId: z.uuid(),
  roomId: z.uuid().nullable(),
  label: z.string(),
  status: z.enum(BED_STATUSES),
  statusReason: z.string().nullable(),
  sortOrder: z.number().int(),
  occupied: z.boolean(),
});

export const adminRoomSchema = z.object({
  id: z.uuid(),
  wardId: z.uuid(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
});

export const adminWardSchema = z.object({
  id: z.uuid(),
  facilityId: z.uuid(),
  name: z.string(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
  bedDayItem: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
  /** The store the ward's own stock is kept in (eMAR ward stock); null when none. */
  stockStoreId: z.uuid().nullable(),
  /** A labour ward (docs/plans/phase2-maternity-mch.md §2.1, M5). */
  isLabourWard: z.boolean(),
  rooms: z.array(adminRoomSchema),
  beds: z.array(adminBedSchema),
});

export type AdminWard = z.infer<typeof adminWardSchema>;
export type AdminBed = z.infer<typeof adminBedSchema>;

export const createWardBodySchema = z.object({
  name,
  /** An active catalogue item in the `bed_day` category. */
  bedDayServiceItemId: z.uuid(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export const updateWardBodySchema = z
  .object({
    name: name.optional(),
    bedDayServiceItemId: z.uuid().optional(),
    status: z.enum(['active', 'inactive']).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    /** A store of the facility for the ward's stock; null takes it away. */
    stockStoreId: z.uuid().nullable().optional(),
    isLabourWard: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');

export const createRoomBodySchema = z.object({ name });

export const updateRoomBodySchema = z
  .object({ name: name.optional(), status: z.enum(['active', 'inactive']).optional() })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change');

export const createBedBodySchema = z.object({
  label: bedLabel,
  roomId: z.uuid().nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

/**
 * Settings changes to a bed. Retiring needs a reason and an empty bed; bringing a retired
 * bed back makes it available.
 */
export const updateBedBodySchema = z
  .object({
    label: bedLabel.optional(),
    roomId: z.uuid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    retired: z.boolean().optional(),
    reason: reasonSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), 'Nothing to change')
  .refine((v) => v.retired !== true || v.reason !== undefined, {
    message: 'Retiring a bed needs a reason',
    path: ['reason'],
  });

// --- The bed board --------------------------------------------------------------------------

/** Who is in a bed, or waiting for one. `patient` is null when the reader may not see them. */
export const boardOccupantSchema = z.object({
  admissionId: z.uuid(),
  admittedAt: z.iso.datetime(),
  /** The admission day is day 1 (EAT days). */
  daysAdmitted: z.number().int().min(1),
  patient: z
    .object({
      id: z.uuid(),
      displayName: z.string(),
      patientNumber: z.string(),
      sex: z.string(),
      age: ageSchema.nullable(),
    })
    .nullable(),
  /**
   * Precautions in place, in words, for a patient the reader may see (docs/plans/
   * phase3-operations-modules.md §2.1). Passive. Absent from older responses.
   */
  precautions: z.array(z.string()).optional(),
});

export const boardBedSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  roomName: z.string().nullable(),
  state: z.enum(BED_BOARD_STATES),
  /** Why a bed is blocked. */
  statusReason: z.string().nullable(),
  occupant: boardOccupantSchema.nullable(),
  /**
   * The open cleaning task of a bed waiting for cleaning (docs/plans/
   * phase3-kitchen-and-housekeeping.md §2.1): what it waits for, in words. No patient.
   * Absent from older responses.
   */
  cleaning: z
    .object({
      taskId: z.uuid(),
      kind: z.enum(['bed_clean', 'terminal_clean']),
      status: z.enum(HOUSEKEEPING_TASK_STATUSES),
      /** "waiting for housekeeping", "being cleaned", "terminal clean needed". */
      words: z.string(),
      since: z.iso.datetime(),
      precautions: z.array(z.string()),
    })
    .nullable()
    .optional(),
});

export const boardWardSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  beds: z.array(boardBedSchema),
  /** Admitted to this ward with no bed yet, earliest first. */
  waiting: z.array(boardOccupantSchema),
  counts: z.object({
    occupied: z.number().int(),
    free: z.number().int(),
    cleaning: z.number().int(),
    blocked: z.number().int(),
  }),
});

export const bedBoardSchema = z.object({ wards: z.array(boardWardSchema) });

export type BedBoard = z.infer<typeof bedBoardSchema>;
export type BoardWard = z.infer<typeof boardWardSchema>;
export type BoardBed = z.infer<typeof boardBedSchema>;
export type BoardOccupant = z.infer<typeof boardOccupantSchema>;

export const setBedStatusBodySchema = z
  .object({
    status: z.enum(BOARD_SETTABLE_STATUSES),
    reason: reasonSchema.optional(),
    /** With `cleaning`: a terminal clean rather than a routine one (housekeeping plan §2.1). */
    terminal: z.boolean().optional(),
  })
  .refine((v) => v.status !== 'blocked' || v.reason !== undefined, {
    message: 'Blocking a bed needs a reason',
    path: ['reason'],
  });

// --- Admissions -----------------------------------------------------------------------------

export const admitBodySchema = z.object({
  /** Generated once per admission; a retried request returns the same stay. */
  clientRequestId: z.uuid(),
  wardId: z.uuid(),
  /** A free bed in the ward, or absent to wait for one. */
  bedId: z.uuid().optional(),
  reason: clinicalText,
  /**
   * Taking a bed still waiting for a routine clean, and why (housekeeping plan H3). A bed
   * waiting for a terminal clean is never taken (H4).
   */
  cleaningOverrideReason: reasonSchema.optional(),
});

export type AdmitBody = z.infer<typeof admitBodySchema>;

export const moveBodySchema = z.object({
  wardId: z.uuid(),
  /** A free bed in the ward, or null to wait for one there. */
  bedId: z.uuid().nullable(),
  /** Required when moving a patient who already has a bed (a transfer). */
  reason: reasonSchema.optional(),
  /** Taking a bed still waiting for a routine clean, and why (housekeeping plan H3). */
  cleaningOverrideReason: reasonSchema.optional(),
});

export const dischargeBodySchema = z.object({
  /** TODO(clinical-verify) B9: the provisional list. */
  disposition: z.enum(DISCHARGE_DISPOSITIONS),
  /** TODO(clinical-verify) B10: required, as the minimal discharge summary. */
  condition: clinicalText,
  summary: clinicalText,
  instructions: clinicalText.optional(),
});

export type DischargeBody = z.infer<typeof dischargeBodySchema>;

export const cancelAdmissionBodySchema = z.object({ reason: reasonSchema });

export const locationSchema = z.object({
  id: z.uuid(),
  ward: z.object({ id: z.uuid(), name: z.string() }),
  bed: z.object({ id: z.uuid(), label: z.string() }).nullable(),
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
  endReason: z.enum(['moved', 'discharged', 'cancelled']).nullable(),
  reason: z.string().nullable(),
});

export const bedDayViewSchema = z.object({
  id: z.uuid(),
  serviceDate: isoDaySchema,
  wardName: z.string(),
  chargeStatus: z.enum(['pending', 'billed', 'cancelled']).nullable(),
  /** The priced total, or null while the charge is not priced. */
  total: amountSchema.nullable(),
});

export const admissionSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visit: z.object({ id: z.uuid(), visitNumber: z.string() }),
  sourceVisit: z.object({ id: z.uuid(), visitNumber: z.string() }),
  status: z.enum(ADMISSION_STATUSES),
  admittedAt: z.iso.datetime(),
  admittingClinician: z.object({ id: z.uuid(), displayName: z.string() }),
  reason: z.string(),
  daysAdmitted: z.number().int().min(1),
  current: locationSchema.nullable(),
  history: z.array(locationSchema),
  bedDays: z.array(bedDayViewSchema),
  discharge: z
    .object({
      dischargedAt: z.iso.datetime(),
      disposition: z.enum(DISCHARGE_DISPOSITIONS),
      condition: z.string(),
      summary: z.string(),
      instructions: z.string().nullable(),
      dischargedBy: z.object({ id: z.uuid(), displayName: z.string() }),
    })
    .nullable(),
  cancelReason: z.string().nullable(),
});

export type AdmissionView = z.infer<typeof admissionSchema>;
export type LocationView = z.infer<typeof locationSchema>;
